/* Adventure feature highlights — Devote Digital, 2026-09-03
 * Web Component following the Enterprise theme pattern.
 * - Reveals rows with IntersectionObserver (the theme's own animate-on-scroll is
 *   disabled in this store's settings, so the section ships its own).
 * - Drives the desktop progress line, active-row dot and a subtle image drift.
 * - Respects prefers-reduced-motion and the theme editor.
 */
if (!customElements.get('feature-highlights')) {
  class FeatureHighlights extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-fh-row]'));
      this.revealTargets = Array.from(this.querySelectorAll('[data-fh-row], [data-fh-reveal]'));
      this.list = this.querySelector('[data-fh-list]');
      this.fill = this.querySelector('.feature-highlights__progress-fill');

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mdUp = window.matchMedia('(min-width: 769px)');

      const wantsReveal = this.dataset.reveal === 'true';
      const canObserve = 'IntersectionObserver' in window;

      if (wantsReveal && canObserve && !this.reducedMotion) {
        this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
          threshold: 0.2,
          rootMargin: '0px 0px -8% 0px'
        });
        this.revealTargets.forEach((el) => this.observer.observe(el));
      } else {
        this.revealTargets.forEach((el) => el.classList.add('is-visible'));
      }

      this.wantsParallax = this.dataset.parallax === 'true' && !this.reducedMotion;
      this.wantsProgress = this.dataset.progress === 'true' && !this.reducedMotion;

      if (this.wantsParallax || this.wantsProgress) {
        this.onScroll = this.onScroll.bind(this);
        this.ticking = false;
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.update();
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = this.onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    handleIntersect(entries, observer) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      const viewportHeight = window.innerHeight;
      const midpoint = viewportHeight / 2;

      if (this.wantsProgress && this.fill && this.list) {
        const rect = this.list.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, (midpoint - rect.top) / rect.height));
        this.fill.style.transform = `scaleY(${progress.toFixed(4)})`;
      }

      this.rows.forEach((row) => {
        const rect = row.getBoundingClientRect();
        const onScreen = rect.bottom > 0 && rect.top < viewportHeight;

        if (this.wantsProgress) {
          row.classList.toggle('is-active', rect.top <= midpoint && rect.bottom >= midpoint);
        }

        if (this.wantsParallax && this.mdUp.matches && onScreen) {
          const centre = rect.top + rect.height / 2;
          const offset = (centre - midpoint) / viewportHeight; // roughly -0.5 … 0.5
          row.style.setProperty('--fh-parallax', `${(offset * -28).toFixed(1)}px`);
        }
      });
    }

    onBlockSelect(evt) {
      if (!this.contains(evt.target)) return;
      evt.target.classList.add('is-visible');
      if (this.observer) this.observer.unobserve(evt.target);
    }
  }

  customElements.define('feature-highlights', FeatureHighlights);
}
